import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import {
  Plus,
  X,
  FileText,
  AlertTriangle,
  ClipboardCheck,
  MessageSquare,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

const quickActions = [
  {
    icon: ClipboardCheck,
    label: 'actions.newAssessment',
    href: '/assessments/new',
    color: 'bg-blue-500 hover:bg-blue-600',
  },
  {
    icon: AlertTriangle,
    label: 'actions.newRisk',
    href: '/risks/new',
    color: 'bg-orange-500 hover:bg-orange-600',
  },
  {
    icon: FileText,
    label: 'actions.newPolicy',
    href: '/governance/policies/new',
    color: 'bg-green-500 hover:bg-green-600',
  },
  {
    icon: MessageSquare,
    label: 'actions.askAI',
    href: '/chat',
    color: 'bg-purple-500 hover:bg-purple-600',
  },
]

export function FloatingMenu() {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handleAction = (href: string) => {
    navigate(href)
    setIsOpen(false)
  }

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <TooltipProvider>
        {/* Quick action buttons */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute bottom-16 right-0 flex flex-col gap-3"
            >
              {quickActions.map((action, index) => (
                <motion.div
                  key={action.label}
                  initial={{ opacity: 0, y: 20, scale: 0.8 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 20, scale: 0.8 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        className={`h-12 w-12 rounded-full shadow-lg ${action.color} text-white`}
                        onClick={() => handleAction(action.href)}
                      >
                        <action.icon className="h-5 w-5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      {t(action.label)}
                    </TooltipContent>
                  </Tooltip>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main toggle button */}
        <motion.div
          animate={{ rotate: isOpen ? 45 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <Button
            size="icon"
            className="h-14 w-14 rounded-full shadow-lg bg-primary hover:bg-primary/90"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? 'Fermer le menu rapide' : 'Ouvrir le menu rapide'}
            aria-expanded={isOpen}
          >
            {isOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Plus className="h-6 w-6" />
            )}
          </Button>
        </motion.div>
      </TooltipProvider>
    </div>
  )
}
